
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import draggable, { MoveEvent } from 'vuedraggable';
import { handleError } from '../../../api';
import { Board, Card, CardsPositionRequest, Ticket } from '../../backend/backendapi';
import { KanbanState, SelectedItemDto } from '../kanban.model';
import { isOwn, isPrivate } from '../kanban.util';
import KanbanCard from './KanbanCard';

@Component({
  name: 'KanbanFlow',
  components: {
    draggable,
    KanbanCard,
  },
})
export default class KanbanFlow extends Vue {
  @Prop({ required: true })
  state!: KanbanState;
  @Prop({ required: true })
  board!: Board;
  @Prop({ required: true })
  flow!: Ticket;

  //runtime
  drag: boolean = false;
  newCard: string | null = null;
  editFlowName: string | null = null;
  collapsed: boolean = false;
  sortBy: keyof Card = 'positionNo';
  sortDirection: 'asc' | 'desc' = 'asc';

  //config
  dragOptions = {
    animation: 200,
    group: 'description',
    disabled: false,
    ghostClass: 'ghost',
  };

  get canCardsBeReordered(): boolean {
    return this.sortBy === 'positionNo';
  }

  get canDelete(): boolean {
    let b = this.state.canDeleteFlows && !this.isInbox;
    b ||= isOwn(this.board) && isPrivate(this.state, this.board);
    return b;
  }

  get canEdit(): boolean {
    let b = this.state.canEditFlows && !this.isInbox;
    b ||= isOwn(this.board) && isPrivate(this.state, this.board);
    return b;
  }

  get cards(): Card[] {
    let cards = [...(this.flow.cards ?? [])]; //map array copy so sort() does not reorder the original array
    if (this.sortBy === 'dueDate') {
      cards.sort((l, r) => (l.dueDate?.getTime() ?? Number.MAX_SAFE_INTEGER) - (r.dueDate?.getTime() ?? Number.MAX_SAFE_INTEGER));
    } else {
      cards.sort((l, r) => ((l.positionNo ?? 0) - (r.positionNo ?? 0)));
    }
    if (this.sortDirection === 'desc') {
      cards.reverse();
    }
    return cards;
  }

  set cards(cards: Card[]) {
    console.log(cards);
    if (!this.canCardsBeReordered) {
      //move newly moved in cards to the end of the list do they get higher positionNo from the backend
      const oldCards = cards.filter(e => e.ticketId === this.flow.id);
      const newCards = cards.filter(e => e.ticketId !== this.flow.id);
      cards = [...oldCards, ...newCards];
    }
    this.flow.cards = cards; //keep ui in new state until update arrives

    this.state.cardsAPI.updatePosition(new CardsPositionRequest({ ticketId: this.flow.id!, cards: cards }))
        .then(newcards => this.flow.cards = newcards.result!);
  }

  get checked(): boolean {
    console.log('flow', this.flow.name, this.cards.every(c => this.state.massSelectionCards.includes(c)));
    return this.cards.every(c => this.state.massSelectionCards.includes(c));
  }

  set checked(val: boolean) {
    console.log('set', val);
    if (!val) {
      this.cards.forEach(c => {
        let index = this.state.massSelectionCards.findIndex(e => e === c);
        if (index > -1) {
          this.state.massSelectionCards.splice(index, 1);
        }
      });
    } else {
      this.cards.forEach(c => {
        let index = this.state.massSelectionCards.findIndex(e => e === c);
        if (index === -1) {
          this.state.massSelectionCards.push(c);
        }
      });
    }
  }

  get isInbox(): boolean {
    return !this.flow.id;
  }

  get visibleOnFilter(): boolean {
    return this.state.filter(new Card({ name: this.flow.name, isPublic: false, updatedOnForReadConfirmation: new Date() })) //TODO: fix filter hack
        || ((this.$refs.kanbanCard as KanbanCard[]) ?? []).some(e => e.visibleOnFilter);
  }

  get lockeddown(): boolean {
    return this.board.lockdownDelay! >= 0 && this.flow.createdOn!.getTime() + this.board.lockdownDelay! * 1000 < new Date().getTime();
  }

  get sortIcon(): string {
    if (this.sortBy == 'positionNo') {
      return 'fa-sort';
    } else if (this.sortDirection == 'asc') {
      return 'fa-sort-up';
    } else {
      return 'fa-sort-down';
    }
  }

  archiveFlow(): void {
    if (this.lockeddown) {
      return;
    }

    //TODO what happens to still open cards
    if (!confirm(this.$t('txtInfoArchive').toString())) {
      return;
    }

    let index = (this.board.tickets ?? []).findIndex(e => e.id === this.flow.id);
    if (index > -1) {
      this.board.tickets?.splice(index, 1);
    }

    this.flow.archived = true;

    //save to backend
    this.state.ticketAPI.archive(this.flow)
        .then(_ => this.$toasted.info(this.$t('txtArchived').toString()))
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  canMove(event: MoveEvent<Card>): boolean {
    const card = event.draggedContext.element; // the moved card
    const otherCard = event.relatedContext.element; //1st card of target flow, may be undefined
    if (!this.canCardsBeReordered) { //this should be the target flow
      if (otherCard && card.ticketId === otherCard.ticketId) {
        return false; //prevent reordering in this flow
      } else {
        return true; //allow moving cards into this flow
      }
    }
    return true;
  }

  click(): void {
    this.collapsed = !this.collapsed;
  }

  createCard(): void {
    if (!this.newCard)
      return;

    let card = new Card();
    card.createdOn = new Date();
    card.ticketId = this.flow.id;
    card.name = this.newCard;

    this.state.cardsAPI.create(card)
        .then(newcard => {
          this.$toasted.info(this.$t('txtCreated').toString());
          if (!this.flow.cards)
            this.flow.cards = [];
          this.flow.cards.push(newcard.result!);

          this.newCard = null;
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  cycleSortBy(): void {
    if (this.sortDirection == 'asc' && this.sortBy !== 'positionNo') { //only flip when not positionNo
      this.sortDirection = 'desc';
    } else {
      const order: (keyof Card)[] = ['positionNo', 'dueDate'];
      const index = (order.indexOf(this.sortBy) + 1) % order.length;
      this.sortBy = order[index];
      this.sortDirection = 'asc';
    }
    console.log('sort', this.sortBy, this.sortDirection);
  }

  deleteFlow(): void {
    if (this.lockeddown) {
      return;
    }

    if (confirm(this.$t('txtInfoDelete').toString())) {
      this.state.ticketAPI.delete(this.flow.id!)
          .then(_ => {
            this.$toasted.info(this.$t('txtDeleted').toString());
            let index = (this.board.tickets ?? []).findIndex(e => e.id === this.flow.id);
            if (index > -1) {
              this.board.tickets?.splice(index, 1);
            }
          })
          .catch(e => handleError(this.$toasted, this.$i18n, e));
    }
  }

  editName(): void {
    if (this.lockeddown) {
      return;
    }

    this.editFlowName = this.flow.name;
    this.$nextTick(() => (this.$refs.editflowname! as HTMLInputElement).focus());
  }

  renameFlow(): void {
    if (!this.editFlowName) {
      return;
    }

    let flow = this.flow.clone();
    flow.name = this.editFlowName;
    this.state.ticketAPI.update(flow)
        .then(_ => {
          this.$toasted.info(this.$t('txtSaved').toString());
          this.flow.name = flow.name;
          this.editFlowName = null;
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  unarchiveFlow(): void {
    if (this.lockeddown) {
      return;
    }

    let index = (this.board.tickets ?? []).findIndex(e => e.id === this.flow.id);
    if (index > -1) {
      this.board.tickets?.splice(index, 1);
    }

    this.flow.archived = false;

    //save to backend
    this.state.ticketAPI.archive(this.flow)
        .then(_ => this.$toasted.info(this.$t('txtArchived').toString()))
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  @Watch('state.selectedViaLog', { deep: true })
  onSelectedLogChanged(log: SelectedItemDto | null): void {
    this.$nextTick(() => {
      if (log?.flowId !== this.flow.id) {
        this.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    });
  }
}
